import { useCallback, useEffect, useRef } from 'react';
import { usePlayerContext } from '../PlayerContext';
import { useStore } from '../hooks/useStore';
import { useEventBus } from '../hooks/useEventBus';
import { Icon18Plus } from '../icons';
import type { ApiData, UserData } from '../../core/types';
import styles from './AdultWarning.module.css';

const LS_PREFIX = 'hide_adult_warning_';

function getAdultDismissTime(streamKey: string): number {
  try {
    return parseInt(localStorage.getItem(LS_PREFIX + streamKey) ?? '', 10) || 0;
  } catch {
    return 0;
  }
}

function setAdultDismissTime(streamKey: string): void {
  try {
    localStorage.setItem(LS_PREFIX + streamKey, String(Date.now()));
  } catch {
    // localStorage unavailable
  }
}

export function AdultWarning() {
  const { store, eventBus, config } = usePlayerContext();
  const show = useStore(store, useCallback(s => s.showAdultWarning, []));
  const streamKeyRef = useRef(config.streamKey);
  streamKeyRef.current = config.streamKey;
  const isOnline = useStore(store, useCallback(s => s.isOnline, []));
  // Listen for api:data — decide whether to show warning
  const handleApiData = useCallback((data: ApiData) => {
    if (!data.adult) return;
    if (!isOnline) return;

    // Subscribers are exempt — user is already in store by the time api:data fires
    if (store.getState().user?.subscribed) {
      eventBus.emit('play');
      return;
    }

    // Skip warning for this stream key if it was ever dismissed before.
    const dismissedAt = getAdultDismissTime(streamKeyRef.current);
    if (dismissedAt) {
      eventBus.emit('play');
      return;
    }

    store.setState({ showAdultWarning: true });
  }, [eventBus, isOnline, store]);

  useEventBus(eventBus, 'api:data', handleApiData);

  // Listen for api:user — subscribers are exempt
  const handleApiUser = useCallback((user: UserData) => {
    if (user.subscribed && store.getState().showAdultWarning) {
      store.setState({ showAdultWarning: false });
      eventBus.emit('play');
    }
  }, [store, eventBus]);

  useEventBus(eventBus, 'api:user', handleApiUser);

  // React to store user changes (e.g. user already present before api:data fires)
  useEffect(() => {
    return store.subscribe(
      s => s.user,
      (user) => {
        if (user?.subscribed && store.getState().showAdultWarning) {
          store.setState({ showAdultWarning: false });
          eventBus.emit('play');
        }
      },
    );
  }, [store, eventBus]);

  // Reset warning on new api:data if stream restarted (channel_start changed)
  // This is already handled by handleApiData re-evaluating the condition

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      store.setState({ showAdultWarning: false });
    };
  }, [store]);

  const handleDismiss = useCallback(() => {
    store.setState({ showAdultWarning: false });
    setAdultDismissTime(streamKeyRef.current);
    eventBus.emit('adult:dismiss');
    eventBus.emit('play');
  }, [store, eventBus]);

  return (
    <div className={`${styles.overlay}${show ? ` ${styles.visible}` : ''}`}>
      <div className={styles.badge}>
        <Icon18Plus />
      </div>
      <div className={styles.message}>
        <p className={styles.title}>Трансляция может содержать материалы 18+.</p>
        <p className={styles.description}>Нажимая «Продолжить», вы подтверждаете, что вам исполнилось 18 лет и вы согласны с просмотром такого контента.</p>
      </div>
      <button className={styles.button} onClick={handleDismiss} type="button">
        Продолжить
      </button>
    </div>
  );
}
